import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { getTechSchedule, type TechScheduleRow, type TechScheduleResponse } from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function getTodayYmdEastern(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;

  if (!year || !month || !day) {
    return new Date().toISOString().slice(0, 10);
  }

  return `${year}-${month}-${day}`;
}

function formatTimeEastern(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getRowLabel(row: TechScheduleRow): { badge: string | null; badgeClass: string } {
  if (row.timestamps.completed_at) {
    return { badge: 'Completed', badgeClass: 'bg-gray-100 text-gray-800' };
  }
  if (row.timestamps.onsite_at) {
    return { badge: 'On site', badgeClass: 'bg-emerald-100 text-emerald-800' };
  }
  if (row.timestamps.dispatched_at) {
    return { badge: 'Dispatched', badgeClass: 'bg-blue-100 text-blue-800' };
  }

  const status = row.job.status ? String(row.job.status) : '';
  if (status) {
    return { badge: status, badgeClass: 'bg-amber-100 text-amber-800' }; // neutral-ish default
  }

  return { badge: null, badgeClass: 'bg-gray-100 text-gray-800' };
}

export default function TechSchedulePage() {
  const navigate = useNavigate();
  const { accessToken } = useTechAuth();
  const [date, setDate] = useState(getTodayYmdEastern);
  const [data, setData] = useState<TechScheduleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => data?.rows || [], [data?.rows]);

  const load = async (targetDate: string) => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getTechSchedule(accessToken, { date: targetDate });
      setData(res);
    } catch (e) {
      setData(null);
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, date]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
          <p className="mt-1 text-gray-600">Your scheduled calls for the selected day (America/New_York).</p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm"
          />
          <button
            type="button"
            onClick={() => load(date)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {data?.warnings?.length ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
          <div className="text-sm text-amber-900">{data.warnings.join(' ')}</div>
        </div>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="text-sm text-gray-600">Jobs: {rows.length}</div>
        </div>

        <div className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <div className="p-6 text-gray-600">No jobs found for this day.</div>
          ) : (
            rows.map((row, idx) => {
              const time = formatTimeEastern(row.timestamps.scheduled_for);
              const customerName = [row.customer?.first_name, row.customer?.last_name].filter(Boolean).join(' ');
              const line2 = [
                [row.location?.city, row.location?.state].filter(Boolean).join(', '),
                row.location?.zipCode,
              ]
                .filter(Boolean)
                .join(' ');

              const { badge, badgeClass } = getRowLabel(row);

              const jobId = row.job.id;
              const canOpen = Boolean(jobId);

              return (
                <button
                  key={`${row.job.id || 'unknown'}-${idx}`}
                  type="button"
                  disabled={!canOpen}
                  onClick={() => {
                    if (!jobId) return;
                    navigate(`/job/${encodeURIComponent(jobId)}?date=${encodeURIComponent(date)}`);
                  }}
                  className={[
                    'w-full text-left p-5 transition-colors',
                    canOpen ? 'hover:bg-gray-50' : 'opacity-60 cursor-not-allowed',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-500">{time ? time : '—'}</div>
                        {badge ? (
                          <span className={['px-2 py-0.5 rounded-full text-xs font-semibold', badgeClass].join(' ')}>
                            {badge}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-1 text-lg font-semibold text-gray-900">
                        {row.job.number ? `Job #${row.job.number}` : row.job.id ? `Job ${row.job.id}` : 'Job'}
                      </div>
                      {customerName ? <div className="mt-1 text-sm font-medium text-gray-800">{customerName}</div> : null}
                      <div className="mt-1 text-sm text-gray-700">{row.location?.address1 || '—'}</div>
                      {line2 ? <div className="text-sm text-gray-500">{line2}</div> : null}
                    </div>

                    <div className="text-sm text-gray-500 whitespace-nowrap">
                      {row.job.type ? row.job.type : ''}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
